import React, { useCallback, useMemo } from "react";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { LocationsPayload } from "./types";
import {
  DEFAULT_COUNTRY_FOR_STATE,
  LOCATION_TYPES,
  TOGGLE_BUTTON_OPTIONS,
} from "./constants";
import { CountryInput } from "./CountryInput";
import { RegionInput } from "./RegionInput";
import { StateInput } from "./StateInput";
import { ExcludedCountriesSection } from "./ExcludedCountriesSection";
import CollectionSelect from "../../CollectionSelect/CollectionSelect";

export interface LocationsFilterConfig {
  collectionSelect?: { enabled?: boolean };
  countrySelect?: { disabledForState?: boolean };
}

export interface LocationsFilterProps {
  selectedValues: LocationsPayload;
  onValuesChange: (payload: LocationsPayload) => void;
  showExcluded?: boolean;
  config?: LocationsFilterConfig;
}

export const LocationsFilter: React.FC<LocationsFilterProps> = ({
  selectedValues,
  onValuesChange,
  showExcluded = false,
  config,
}) => {
  const isCollectionSelectEnabled = config?.collectionSelect?.enabled !== false;
  const isCountrySelectDisabledForState =
    config?.countrySelect?.disabledForState ?? false;
  const handleLocationTypeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newType: string | null) => {
      if (!newType) return; // Handle null case when clicking the same button

      const locationType = newType as LocationsPayload["selectedLocationType"];

      // Clear arrays based on new location type
      const newPayload: LocationsPayload = {
        selectedLocationType: locationType,
        countryAlpha2s: [],
        regionIds: [],
        stateIds: [],
        excludedCountryAlpha2s: [],
      };

      // Preserve existing data based on type
      switch (locationType) {
        case LOCATION_TYPES.COUNTRY:
          newPayload.countryAlpha2s = selectedValues.countryAlpha2s;
          newPayload.excludedCountryAlpha2s =
            selectedValues.excludedCountryAlpha2s;
          break;
        case LOCATION_TYPES.REGION:
          newPayload.regionIds = selectedValues.regionIds;
          newPayload.excludedCountryAlpha2s =
            selectedValues.excludedCountryAlpha2s;
          break;
        case LOCATION_TYPES.STATE:
          // For state, keep first country if exists
          newPayload.countryAlpha2s =
            selectedValues.countryAlpha2s.length > 0
              ? [selectedValues.countryAlpha2s[0]]
              : [];
          newPayload.stateIds = selectedValues.stateIds;
          break;
      }

      onValuesChange(newPayload);
    },
    [selectedValues, onValuesChange]
  );

  const handleCountrySelect = useCallback(
    (countryAlpha2s: string[]) => {
      const newPayload: LocationsPayload = {
        ...selectedValues,
        countryAlpha2s,
      };

      // If in state mode and selecting a different country, clear states
      if (
        selectedValues.selectedLocationType === LOCATION_TYPES.STATE &&
        countryAlpha2s[0] !== selectedValues.countryAlpha2s[0]
      ) {
        newPayload.stateIds = [];
      }

      onValuesChange(newPayload);
    },
    [selectedValues, onValuesChange]
  );

  const handleRegionSelect = useCallback(
    (regionIds: string[]) => {
      onValuesChange({
        ...selectedValues,
        regionIds,
      });
    },
    [selectedValues, onValuesChange]
  );

  const handleStateSelect = useCallback(
    (countryAlpha2: string, stateIds: string[]) => {
      onValuesChange({
        ...selectedValues,
        countryAlpha2s: [countryAlpha2],
        stateIds,
      });
    },
    [selectedValues, onValuesChange]
  );

  const handleExcludedCountrySelect = useCallback(
    (excludedCountryAlpha2s: string[]) => {
      onValuesChange({
        ...selectedValues,
        excludedCountryAlpha2s,
      });
    },
    [selectedValues, onValuesChange]
  );

  const renderLocationInput = useMemo(() => {
    switch (selectedValues.selectedLocationType) {
      case LOCATION_TYPES.COUNTRY:
        return (
          <Stack direction="column" gap={0.5} width="100%">
            <CountryInput
              selectedItems={selectedValues.countryAlpha2s}
              onCountrySelect={handleCountrySelect}
            />

            {isCollectionSelectEnabled && (
              <CollectionSelect
                placeholder="Search collections"
                type="country"
                onSelect={({ ids }) =>
                  handleCountrySelect([
                    ...(selectedValues.countryAlpha2s as string[]),
                    ...(ids as string[]).filter(
                      (id) => !selectedValues.countryAlpha2s.includes(id)
                    ),
                  ])
                }
              />
            )}

            {showExcluded && (
              <ExcludedCountriesSection
                selectedItems={selectedValues.excludedCountryAlpha2s || []}
                onCountrySelect={handleExcludedCountrySelect}
              />
            )}
          </Stack>
        );
      case LOCATION_TYPES.REGION:
        return (
          <Stack direction="column" spacing={2} width="100%">
            <RegionInput
              selectedItems={selectedValues.regionIds}
              onRegionSelect={handleRegionSelect}
            />

            {showExcluded && (
              <ExcludedCountriesSection
                selectedItems={selectedValues.excludedCountryAlpha2s || []}
                onCountrySelect={handleExcludedCountrySelect}
              />
            )}
          </Stack>
        );
      case LOCATION_TYPES.STATE:
        return (
          <Stack direction="column" gap={0.5}>
            <CountryInput
              selectedItems={
                selectedValues.countryAlpha2s.length > 0
                  ? selectedValues.countryAlpha2s
                  : [DEFAULT_COUNTRY_FOR_STATE]
              }
              onCountrySelect={handleCountrySelect}
              isMultiple={false}
              disabled={isCountrySelectDisabledForState}
            />

            <StateInput
              selectedItems={selectedValues.stateIds}
              countryAlpha2={
                selectedValues.countryAlpha2s[0] || DEFAULT_COUNTRY_FOR_STATE
              }
              onStateSelect={handleStateSelect}
            />

            {isCollectionSelectEnabled && (
              <CollectionSelect
                placeholder="Search collections"
                type="state"
                onSelect={({ ids }) =>
                  handleStateSelect(selectedValues.countryAlpha2s[0], [
                    ...(selectedValues.stateIds as string[]),
                    ...(ids as string[]).filter(
                      (id) => !selectedValues.stateIds.includes(id)
                    ),
                  ])
                }
              />
            )}
          </Stack>
        );
    }
  }, [
    selectedValues,
    handleCountrySelect,
    handleRegionSelect,
    handleStateSelect,
    handleExcludedCountrySelect,
    showExcluded,
    isCollectionSelectEnabled,
    isCountrySelectDisabledForState,
  ]);

  return (
    <Stack direction="column" gap={0.5}>
      <ToggleButtonGroup
        size="small"
        color="secondary"
        value={selectedValues.selectedLocationType}
        onChange={handleLocationTypeChange}
        fullWidth
        exclusive
      >
        {TOGGLE_BUTTON_OPTIONS.map((option) => (
          <ToggleButton key={option} value={option}>
            {option}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Stack>{renderLocationInput}</Stack>
    </Stack>
  );
};
